import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  InputNumber,
  Table,
  Button,
  Typography,
  Tooltip,
  Empty,
  Input,
  Checkbox,
} from 'antd';

import {
  AFS_GROUP,
  ALTERNATIVES_GROUP,
  AssetGroupAllocationPayload,
  BONDS_GROUP,
  CASH_APPROACH,
  CASH_GROUP,
  createUpdateGroupAllocations,
  CRYPTO_GROUP,
  EQUITIES_GROUP,
  FX_GROUP,
  GROUP_ASSET_NAME,
  HTM_GROUP,
  LIMIT_METRIC_ALPHA,
  LIMIT_METRIC_BETA,
  LIMIT_METRIC_CHOICES,
  LIMIT_METRIC_DURATION,
  LIMIT_METRIC_MDD,
  LIMIT_METRIC_PORTFOLIO,
  LIMIT_METRIC_SHARPE,
  LIMIT_METRIC_TREYNOR,
  LIMIT_METRIC_VAR,
  LIMIT_METRIC_VOLATILITY,
  MetricsLimitsNew,
  selectors,
  updateMetricsLimits,
} from 'state';
import { red } from '@ant-design/colors';
import { InfoCircleOutlined } from '@ant-design/icons';
import { formatters } from 'services';
import { useMetricsKeys } from '../../TraineeSession/useMetricsData';

type MetricLimitsWithVisible = MetricsLimitsNew & {
  visible: boolean;
};

type State = {
  [metricType: number]: MetricLimitsWithVisible;
};

// type State = {
//   [metricType: number]: MetricsLimitsNew;
// };

export const metricInputSettings: { [key: number]: any } = {
  [LIMIT_METRIC_VAR]: {
    step: 100_000,
    min: -100_000_000,
    max: 100_000_000,
    formatter: (value: number) => `${formatters.commasNoDigits(value)}`,
    player_graph_formatter: (value: number) => `${formatters.million(value)}`,
    parser: (value: string) => Number(value.replace(/,/g, '')),
  },
  [LIMIT_METRIC_SHARPE]: {
    step: 0.1,
    min: -10,
    max: 10,
    formatter: (value: number) => `${value}`,
    player_graph_formatter: (value: number) =>
      `${formatters.commas2Digits(value)}`,
    parser: (value: number) => value,
  },
  [LIMIT_METRIC_TREYNOR]: {
    step: 0.1,
    min: -10,
    max: 10,
    formatter: (value: number) => `${value}`,
    player_graph_formatter: (value: number) =>
      `${formatters.commas2Digits(value)}`,
    parser: (value: number) => value,
  },
  [LIMIT_METRIC_ALPHA]: {
    step: 0.01,
    min: -10,
    max: 10,
    formatter: (value: number) => `${Math.round(value * 100)}%`,
    player_graph_formatter: (value: number) => `${Math.round(value * 100)}%`,
    parser: (value: string | undefined) => {
      const parsedValue = `${value?.replaceAll('%', '')}`;
      const nr = Number(parsedValue);
      if (!isNaN(nr)) {
        return (nr / 100).toFixed(2);
      }
      return '';
    },
  },
  [LIMIT_METRIC_BETA]: {
    step: 0.1,
    min: -10,
    max: 10,
    player_graph_formatter: (value: number) =>
      `${formatters.commas2Digits(value)}`,
    formatter: (value: number) => `${value}`,
    parser: (value: string) => value,
  },
  [LIMIT_METRIC_VOLATILITY]: {
    step: 0.01,
    min: -10,
    max: 10,
    formatter: (value: number) => `${Math.round(value * 100)}%`,
    player_graph_formatter: (value: number) => `${Math.round(value * 100)}%`,
    parser: (value: string | undefined) => {
      const parsedValue = `${value?.replaceAll('%', '')}`;
      const nr = Number(parsedValue);
      if (!isNaN(nr)) {
        return (nr / 100).toFixed(2);
      }
      return '';
    },
  },
  [LIMIT_METRIC_MDD]: {
    step: 0.01,
    min: -2,
    max: 0,
    formatter: (value: number) => `${Math.round(value * 100)}%`,
    player_graph_formatter: (value: number) => `${Math.round(value * 100)}%`,
    parser: (value: string | undefined) => {
      const parsedValue = `${value?.replaceAll('%', '')}`;
      const nr = Number(parsedValue);
      if (!isNaN(nr)) {
        return (nr / 100).toFixed(2);
      }
      return '';
    },
  },
};

const MetricsLimits2: React.FC<{
  allowEditing: boolean;
}> = ({ allowEditing }) => {
  const dispatch = useDispatch();
  const { metrics_limits, display_metrics } = useSelector(
    selectors.activeSession
  );

  console.log(metrics_limits);

  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [metricsLimits, setMetricsLimits] = useState<State>({});
  const metricTypes = { ...LIMIT_METRIC_CHOICES };
  const reset = () => {
    const newMetricsLimitsObjs = {} as State;
    metrics_limits.forEach(a => {
      newMetricsLimitsObjs[a.metric_type] = {
        id: a.id,
        metric_type: a.metric_type,
        lower_limit: a.lower_limit,
        upper_limit: a.upper_limit,
        enforced: a.enforced,
        // visible: !!display_metrics?.includes(a.metric_type),
        visible: a.visible,
      };
    });
    setMetricsLimits(newMetricsLimitsObjs);
    setErrors([]);
  };

  useEffect(reset, [metrics_limits]);
  // const visibleMetrics = useMetricsKeys();
  // console.log(visibleMetrics);
  const dataSource = Object.entries(metricTypes)
    // .filter(([key]) => display_metrics.includes(parseInt(key)))
    .filter(([key]) => {
      // console.log(key);
      // console.log(typeof key);
      if (metrics_limits[Number(key)] !== undefined) return true;
    })
    .map(([key, name]) => {
      const metricType = parseInt(key);
      // const metrics_limits_with_visible:MetricLimitsWithVisible[] = metrics_limits.map(item => )
      // console.log(display_metrics);
      // console.log(metrics_limits);
      const allocation = metrics_limits.find(a => a.metric_type === metricType);

      return { name, metricType, allocation };
    });
  if (dataSource.length === 0) return <div></div>;
  console.log(dataSource);

  const getProps = (
    field: 'lower_limit' | 'upper_limit' | 'enforced' | 'visible'
  ) => {
    return {
      render(_: unknown, record: (typeof dataSource)[0]) {
        const metricType = record.metricType;
        if (metricType === undefined) return;
        const settings = metricInputSettings[metricType] || {};
        if (editing) {
          const metricsLimitsObj = metricsLimits[metricType];
          const limit = metricsLimitsObj[field];
          if (field === 'enforced') {
            let disabled = false;
            if (
              record.metricType == LIMIT_METRIC_DURATION ||
              record.metricType == LIMIT_METRIC_PORTFOLIO
            ) {
              disabled = true;
            }
            return (
              <div style={{ textAlign: 'center' }}>
                <Checkbox
                  disabled={disabled}
                  checked={Boolean(limit)}
                  onChange={value => {
                    if (value === undefined) {
                      return;
                    }
                    setMetricsLimits({
                      ...metricsLimits,
                      [metricType]: {
                        ...metricsLimitsObj,
                        metric_type: metricType,
                        [field]: value.target.checked,
                      },
                    });
                  }}
                />
              </div>
            );
          }
          if (field === 'visible') {
            // console.log(`limit: ${limit}`);
            // console.log(`metricsLimits: ${metricsLimits}`);
            // console.log(`metricsLimitsObj: ${metricsLimitsObj}`);
            // console.log(`type: ${typeof limit}`);
            return (
              <div style={{ textAlign: 'center' }}>
                <Checkbox
                  checked={Boolean(limit)}
                  onChange={value => {
                    if (value === undefined) {
                      return;
                    }
                    setMetricsLimits({
                      ...metricsLimits,
                      [metricType]: {
                        ...metricsLimitsObj,
                        metric_type: metricType,
                        [field]: value.target.checked,
                      },
                    });
                  }}
                />
              </div>
            );
          }

          return (
            <div>
              {limit !== null ? (
                <InputNumber
                  value={limit}
                  size={'small'}
                  style={{ width: '100%' }}
                  {...settings}
                  onChange={value => {
                    if (value === undefined) {
                      return;
                    }
                    setMetricsLimits({
                      ...metricsLimits,
                      [metricType]: {
                        ...metricsLimitsObj,
                        metric_type: metricType,
                        [field]: value,
                      },
                    });
                  }}
                  // step={0.01}
                  // min={0}
                  // max={1}
                  // formatter={value => {
                  //   const nr = Number(value) || 0;
                  //   return `${Math.round(nr * 100)}%`;
                  // }}
                  // parser={value => {
                  //   const parsedValue = `${value?.replaceAll('%', '')}`;
                  //   const nr = Number(parsedValue);
                  //   if (!isNaN(nr)) {
                  //     return (nr / 100).toFixed(2);
                  //   }
                  //   return '';
                  // }}
                />
              ) : (
                <Input
                  size={'small'}
                  style={{ textAlign: 'center' }}
                  disabled={true}
                  value={'NA'}
                />
              )}
            </div>
          );
        }

        if (!record.allocation) {
          return <div>-</div>;
        }
        if (record.allocation[field] !== null) {
          // if (true) {
          if (field !== 'enforced' && field !== 'visible') {
            return (
              <div style={{ textAlign: 'center' }}>
                {settings.player_graph_formatter(record.allocation[field])}
              </div>
            );
            // return settings.player_graph_formatter(3);
            // return 3;
          } else {
            if (record.allocation[field]) {
              return <div style={{ textAlign: 'center' }}>Yes</div>;
            } else {
              return <div style={{ textAlign: 'center' }}>No</div>;
            }
          }
        } else {
          return <div style={{ textAlign: 'center' }}>NA</div>;
        }
        // return (
        //   <div>
        //     {record.allocation[field] !== null ? (
        //
        //     ) : (
        //       // record.allocation[field]
        //       <div style={{ textAlign: 'center' }}>NA</div>
        //     )}
        //   </div>
        // );
      },
    };
  };

  // const isValid = () => {
  //   const errorsList = [];
  //
  //   const valuesAscending = Object.values(metricsLimits).reduce(
  //     (acc, { lower_limit, upper_limit }) => {
  //       // Skip undefined cells
  //       if (!lower_limit && !upper_limit) {
  //         return acc;
  //       }
  //       return acc && (lower_limit ?? 0) < (upper_limit ?? 0);
  //     },
  //     true
  //   );
  //
  //   if (!valuesAscending) {
  //     errorsList.push(
  //       'Lower limit values must be lesser than upper limit values!'
  //     );
  //   }
  //
  //   if (errorsList.length) {
  //     setErrors(errorsList);
  //     return false;
  //   }
  //
  //   return true;
  // };

  const editButton = (
    <div
      style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
    >
      {!editing && (
        <Button
          onClick={() => {
            setEditing(true);
          }}
          style={{ position: 'absolute', right: 0 }}
        >
          Edit
        </Button>
      )}
      {editing && (
        <>
          <Button
            type="primary"
            onClick={() => {
              // if (!isValid()) {
              //   return;
              // }

              setEditing(false);
              dispatch(
                updateMetricsLimits({
                  limits: Object.values(metricsLimits),
                })
              );
            }}
            style={{ position: 'absolute', right: 90 }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setEditing(false);
              reset();
            }}
            style={{ position: 'absolute', right: 0 }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );
  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          Select and save some instruments.
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };
  return (
    <div style={{ minWidth: 250 }}>
      <Typography.Title level={4} style={{ display: 'inline-block' }}>
        Metrics Limits
      </Typography.Title>
      <Table
        rowKey="metric"
        dataSource={dataSource}
        columns={[
          { title: 'Metric', dataIndex: 'name', width: 40 },
          {
            title: 'Enforce limits',
            // ...getProps(),
            children: [
              {
                title: 'Lower',
                ...getProps('lower_limit'),
                width: 120,
              },
              { title: 'Upper', ...getProps('upper_limit') },
              { title: 'Enforced', ...getProps('enforced'), width: 50 },
            ],
          },
          { title: 'Visible', ...getProps('visible'), width: 50 },
        ]}
        locale={noDataBehaviour}
        pagination={false}
        bordered
        footer={() => {
          if (allowEditing) {
            return editButton;
          } else {
            return <div></div>;
          }
        }}
      />

      {editing && !!errors.length && (
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {errors.map(e => {
            return (
              <div key={e} style={{ color: red[5] }}>
                {e}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MetricsLimits2;
